import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Snowflake, Droplets, Wind, Heart, Waves, Activity, Thermometer, Stethoscope, ChevronRight, Clock, Target, Users, BookOpen, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Contact from '../components/Contact';

const recoveryServices = [
  {
    icon: <Waves className="w-12 h-12" />,
    name: '25m Swimming Pool',
    description: 'Two-lane swimming pool perfect for recovery swims and aqua therapy',
    benefits: ['Low-impact exercise', 'Cardiovascular health', 'Full-body workout', 'Temperature controlled', 'Professional lifeguard'],
    duration: '30-60 minutes',
    image: 'https://i.ibb.co/FknTZrrz/DSC01354.jpg',
    color: 'from-blue-500/20 to-cyan-500/20'
  },
  {
    icon: <Droplets className="w-12 h-12" />,
    name: 'Ice Bath',
    description: 'Cold immersion therapy at 4°C for inflammation reduction and recovery',
    benefits: ['Reduces inflammation', 'Speeds recovery', 'Mental resilience', 'Immune boost', 'Pain relief'],
    duration: '3-10 minutes',
    image: 'https://i.ibb.co/yngNWDxM/DSC01375.jpg',
    color: 'from-purple-500/20 to-pink-500/20'
  },
  {
    icon: <Heart className="w-12 h-12" />,
    name: 'Traditional Sauna',
    description: 'Traditional dry heat therapy at 80-90°C for muscle recovery and detoxification',
    benefits: ['Muscle relaxation', 'Improved circulation', 'Enhanced detoxification', 'Stress reduction', 'Better sleep'],
    duration: '15-20 minutes',
    image: 'https://i.ibb.co/QvNpyQZ9/DSC01358.jpg',
    color: 'from-green-500/20 to-emerald-500/20'
  },
  {
    icon: <Waves className="w-12 h-12" />,
    name: 'Spa Pool',
    description: 'Therapeutic spa pool with massage jets for muscle recovery and relaxation',
    benefits: ['Hydrotherapy', 'Muscle recovery', 'Joint mobility', 'Stress relief', 'Improved circulation'],
    duration: '15-30 minutes',
    image: 'https://i.ibb.co/FL2gfFck/DSC05254.jpg',
    color: 'from-cyan-500/20 to-blue-500/20'
  },
  {
    icon: <Stethoscope className="w-12 h-12" />,
    name: 'Physiotherapy',
    description: 'Professional physiotherapy services for injury prevention and recovery',
    benefits: ['Injury rehabilitation', 'Movement assessment', 'Performance optimization', 'Pain management', 'Recovery planning'],
    duration: '30-60 minutes',
    image: 'https://i.ibb.co/0R40sQZF/IMG-3767.jpg',
    color: 'from-yellow-500/20 to-orange-500/20'
  },
  {
    icon: <Activity className="w-12 h-12" />,
    name: 'Steam Room',
    description: 'Humid heat therapy for respiratory health and muscle relaxation',
    benefits: ['Respiratory health', 'Muscle relaxation', 'Skin cleansing', 'Stress relief', 'Improved breathing'],
    duration: '10-15 minutes',
    image: 'https://i.ibb.co/kCQpt8n/Screenshot-2025-03-11-at-08-57-15.png',
    color: 'from-purple-500/20 to-pink-500/20'
  }
];

const benefits = [
  {
    icon: Clock,
    title: 'Quick Recovery',
    description: 'Accelerate your recovery time with our advanced facilities'
  },
  {
    icon: Target,
    title: 'Performance Boost',
    description: 'Enhance your athletic performance through strategic recovery'
  },
  {
    icon: Users,
    title: 'Expert Guidance',
    description: 'Professional staff to guide you through recovery protocols'
  }
];

export default function RecoveryPage() {
  const [selectedService, setSelectedService] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[url('https://i.ibb.co/Y733h978/DSC01360.jpg')] bg-cover bg-center opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-b from-black via-black/50 to-black" />
            
            {/* Animated Background Elements */}
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 0.15, scale: 1.2 }}
              transition={{ duration: 3, repeat: Infinity, repeatType: "reverse" }}
              className="absolute -top-1/4 -left-1/4 w-1/2 h-1/2 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full blur-3xl"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 0.15, scale: 1.2 }}
              transition={{ duration: 3, delay: 1.5, repeat: Infinity, repeatType: "reverse" }}
              className="absolute -bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-full blur-3xl"
            />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <motion.div
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse" }}
                  className="inline-block mb-6"
                >
                  <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-1 rounded-full shadow-lg shadow-blue-500/20">
                    <div className="bg-black px-6 py-2 rounded-full flex items-center space-x-2">
                      <Heart className="w-5 h-5 text-blue-400" />
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400 font-bold">
                        PREMIUM RECOVERY ZONE
                      </span>
                      <Heart className="w-5 h-5 text-blue-400" />
                    </div>
                  </div>
                </motion.div>

                <h1 className="text-7xl font-bold mb-6">RECOVER LIKE A PRO</h1>
                <p className="text-xl text-gray-400 mb-8">Experience our state-of-the-art recovery facilities designed to optimize your performance and well-being</p>

                <div className="flex flex-wrap gap-4">
                  <a
                    href="#services"
                    className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-cyan-600 px-8 py-4 rounded-full font-bold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 group"
                  >
                    <span>EXPLORE FACILITIES</span>
                    <ChevronRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300" />
                  </a>
                  <Link
                    to="/blog/hot-cold-therapy"
                    className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-8 py-4 rounded-full font-bold hover:bg-white/20 transition-all duration-300"
                  >
                    <BookOpen className="w-5 h-5" />
                    <span>READ OUR GUIDE</span>
                  </Link>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="relative"
              >
                <div className="grid grid-cols-2 gap-4">
                  {[recoveryServices[0], recoveryServices[1], recoveryServices[2], recoveryServices[3]].map((service, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.2, duration: 0.8 }}
                      className="relative group aspect-square"
                    >
                      <div className="absolute inset-0 rounded-2xl overflow-hidden">
                        <img
                          src={service.image}
                          alt={service.name}
                          className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent" />
                      </div>
                      <div className="absolute inset-0 p-4 flex flex-col justify-end">
                        <div className={`bg-gradient-to-br ${service.color} p-2 rounded-full w-fit mb-2`}>
                          {React.cloneElement(service.icon, { className: 'w-4 h-4' })}
                        </div>
                        <h3 className="text-sm font-bold">{service.name}</h3>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gradient-to-b from-black to-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2, duration: 0.8 }}
                  className="relative group"
                >
                  <motion.div
                    animate={{
                      opacity: [0.3, 0.6, 0.3],
                      scale: [1, 1.02, 1]
                    }}
                    transition={{ duration: 3, repeat: Infinity }}
                    className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-2xl blur-xl"
                  />
                  <div className="relative bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 h-full">
                    <benefit.icon className="w-8 h-8 text-blue-400 mb-4" />
                    <h3 className="text-2xl font-bold mb-2">{benefit.title}</h3>
                    <p className="text-gray-400">{benefit.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section id="services" className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-5xl font-bold mb-4">RECOVERY FACILITIES</h2>
              <p className="text-xl text-gray-400">Complete wellness and recovery solutions</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {recoveryServices.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.8 }}
                  className="relative group cursor-pointer"
                  onClick={() => setSelectedService(selectedService === index ? null : index)}
                >
                  <motion.div
                    animate={{
                      opacity: [0.3, 0.6, 0.3],
                      scale: [1, 1.02, 1]
                    }}
                    transition={{ duration: 3, repeat: Infinity }}
                    className={`absolute inset-0 bg-gradient-to-br ${service.color} rounded-2xl blur-xl ${
                      selectedService === index ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                  <div className="relative bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10">
                    <div className="aspect-w-16 aspect-h-9">
                      <img
                        src={service.image}
                        alt={service.name}
                        className="w-full h-[400px] object-cover transform group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent" />
                    </div>
                    <div className="p-8">
                      <div className="flex items-center space-x-4 mb-6">
                        <div className={`bg-gradient-to-br ${service.color} p-4 rounded-full`}>
                          {service.icon}
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold mb-1">{service.name}</h3>
                          <p className="text-gray-400">Duration: {service.duration}</p>
                        </div>
                      </div>
                      
                      <motion.div
                        initial={false}
                        animate={{ height: selectedService === index ? 'auto' : 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <p className="text-gray-300 mb-6">{service.description}</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {service.benefits.map((benefit, i) => (
                            <div
                              key={i}
                              className={`bg-gradient-to-r ${service.color} rounded-xl p-4 transform hover:scale-105 transition-transform duration-300`}
                            >
                              <span className="text-sm">{benefit}</span>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Blog Section */}
        <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-5xl font-bold mb-4">RECOVERY INSIGHTS</h2>
              <p className="text-xl text-gray-400">Learn about our recovery protocols</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative group cursor-pointer"
            >
              <Link to="/blog/hot-cold-therapy">
                <div className="relative bg-gradient-to-br from-blue-500/10 to-cyan-500/10 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="relative aspect-[16/9] lg:aspect-auto">
                      <img
                        src="https://i.ibb.co/Y733h978/DSC01360.jpg"
                        alt="Hot and Cold Therapy"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/50 to-transparent" />
                    </div>
                    <div className="p-8 flex flex-col justify-center">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 p-2 rounded-full">
                          <BookOpen className="w-6 h-6 text-blue-400" />
                        </div>
                        <span className="text-sm text-blue-400 font-semibold">RECOVERY GUIDE</span>
                      </div>
                      <h3 className="text-3xl font-bold mb-4">The Science Behind Hot & Cold Therapy</h3>
                      <p className="text-gray-400 mb-6">
                        Discover how combining hot and cold therapy can enhance your recovery, boost performance, and improve overall well-being. Learn the optimal protocols for using our sauna and ice bath facilities.
                      </p>
                      <div className="flex items-center space-x-4 text-sm text-gray-400">
                        <div className="flex items-center space-x-2">
                          <Clock className="w-4 h-4" />
                          <span>5 min read</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Calendar className="w-4 h-4" />
                          <span>March 20, 2024</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>
        </section>

        <Contact />
      </main>

      <Footer />
    </div>
  );
}